import type { Program, ProgramCreate, ProgramUpdate } from "@/api/client";

/** Inputs hold what was typed; numbers are parsed when the body is built. */
export type ProgramFormValues = {
  name: string;
  program_type: string;
  induction_date: string;
  membership_no_prefix: string;
  membership_no_width: string;
  membership_no_next: string;
  requires_payment: boolean;
  template_id: string;
  /** "" is the system default. */
  email_template_id: string;
};
export type ProgramField = keyof ProgramFormValues;
export const PROGRAM_FIELDS = [
  "name",
  "program_type",
  "induction_date",
  "membership_no_prefix",
  "membership_no_width",
  "membership_no_next",
  "requires_payment",
  "template_id",
  "email_template_id",
] as const satisfies readonly ProgramField[];

export const EMPTY_PROGRAM_FORM: ProgramFormValues = {
  name: "",
  program_type: "",
  induction_date: "",
  membership_no_prefix: "",
  membership_no_width: "5",
  membership_no_next: "1",
  requires_payment: true,
  template_id: "",
  email_template_id: "",
};

export function programFormFrom(program: Program): ProgramFormValues {
  return {
    name: program.name,
    program_type: program.program_type,
    induction_date: program.induction_date ?? "",
    membership_no_prefix: program.membership_no_prefix ?? "",
    membership_no_width: String(program.membership_no_width ?? 5),
    membership_no_next: String(program.membership_no_next ?? 1),
    requires_payment: program.requires_payment,
    template_id: program.template_id ?? "",
    email_template_id: program.email_template_id ?? "",
  };
}

// Number("") is 0, so blank must be checked first.
const parse = (value: string) => (value.trim() === "" ? NaN : Number(value));

/** `floor` is the saved next number on edit: it can only go up. */
export function validateProgramForm(form: ProgramFormValues, floor = 1): Partial<Record<ProgramField, string>> {
  const errors: Partial<Record<ProgramField, string>> = {};
  if (form.name.trim() === "") errors.name = "is required";
  if (form.program_type.trim() === "") errors.program_type = "is required";
  const width = parse(form.membership_no_width);
  if (!(Number.isInteger(width) && width >= 1 && width <= 20)) errors.membership_no_width = "must be a whole number from 1 to 20";
  const next = parse(form.membership_no_next);
  if (!(Number.isInteger(next) && next >= 1)) errors.membership_no_next = "must be a whole number of at least 1";
  else if (next < floor) errors.membership_no_next = `can't be lower than ${floor}`;
  return errors;
}

/** The number the scheme gives out next, as the server formats it (prefix + zero-padded number). */
export function formatMembershipNo(prefix: string, width: number, next: number) {
  return `${prefix}${String(next).padStart(width, "0")}`;
}

/** Null while the inputs don't make a valid number. */
export function previewNumber(form: ProgramFormValues) {
  const width = parse(form.membership_no_width);
  const next = parse(form.membership_no_next);
  if (!(Number.isInteger(width) && width >= 1 && width <= 20 && Number.isInteger(next) && next >= 1)) return null;
  return formatMembershipNo(form.membership_no_prefix.trim(), width, next);
}

export function toCreateBody(form: ProgramFormValues): ProgramCreate {
  const body: ProgramCreate = {
    name: form.name.trim(),
    program_type: form.program_type.trim(),
    membership_no_width: parse(form.membership_no_width),
    membership_no_next: parse(form.membership_no_next),
    requires_payment: form.requires_payment,
  };
  if (form.induction_date !== "") body.induction_date = form.induction_date;
  if (form.membership_no_prefix.trim() !== "") body.membership_no_prefix = form.membership_no_prefix.trim();
  if (form.template_id !== "") body.template_id = form.template_id;
  if (form.email_template_id !== "") body.email_template_id = form.email_template_id;
  return body;
}

/** Only the keys that differ from the saved program; a cleared date or template is null, a blank prefix "". */
export function toUpdateBody(form: ProgramFormValues, program: Program): ProgramUpdate {
  const body: ProgramUpdate = {};
  if (form.name.trim() !== program.name) body.name = form.name.trim();
  if (form.program_type.trim() !== program.program_type) body.program_type = form.program_type.trim();
  if (form.induction_date !== (program.induction_date ?? "")) body.induction_date = form.induction_date || null;
  if (form.membership_no_prefix.trim() !== (program.membership_no_prefix ?? "")) {
    body.membership_no_prefix = form.membership_no_prefix.trim();
  }
  const width = parse(form.membership_no_width);
  if (width !== program.membership_no_width) body.membership_no_width = width;
  const next = parse(form.membership_no_next);
  if (next !== program.membership_no_next) body.membership_no_next = next;
  if (form.requires_payment !== program.requires_payment) body.requires_payment = form.requires_payment;
  if (form.template_id !== (program.template_id ?? "")) body.template_id = form.template_id || null;
  if (form.email_template_id !== (program.email_template_id ?? "")) body.email_template_id = form.email_template_id || null;
  return body;
}
